import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './Timeline.css';

export interface TimelineSegment {
  id: string | number;
  start: Date;
  end: Date;
  label?: string;
  duration?: number; // в часах
  data?: any; // дополнительные данные сегмента
}

export interface TimelineHandle {
  id: string | number;
  time: Date;
  position?: number; // процент от начала (0-100), если не указан, вычисляется автоматически
}

export interface TimelineProps {
  /** Начало временного диапазона */
  startTime: Date;
  /** Конец временного диапазона */
  endTime: Date;
  /** Сегменты для отображения на временной шкале */
  segments: TimelineSegment[];
  /** Точки перетаскивания (опционально) */
  handles?: TimelineHandle[];
  /** Callback при перетаскивании точки */
  onHandleDrag?: (handleId: string | number, newTime: Date) => void;
  /** Callback при завершении перетаскивания */
  onHandleDragEnd?: (handleId: string | number, newTime: Date) => void;
  /** Функция форматирования времени для отображения */
  formatTime?: (date: Date) => string;
  /** Функция форматирования длительности для отображения */
  formatDuration?: (hours: number) => string;
  /** Минимальная ширина сегмента для отображения (в процентах) */
  minSegmentWidth?: number;
  /** Показывать ли время начала и конца */
  showTimeLabels?: boolean;
  /** Показывать ли время на ползунках (по умолчанию как showTimeLabels) */
  showHandleTimeLabels?: boolean;
  /** Показывать ли длительность сегментов */
  showDuration?: boolean;
  /** Показывать ли названия сегментов */
  showLabels?: boolean;
  /** Разрешить ли перетаскивание ползунков */
  draggable?: boolean;
  /** Шаг привязки ползунка в минутах (0 — без привязки) */
  snapMinutes?: number;
  /** Дополнительный CSS класс */
  className?: string;
  /** Callback для получения ref контейнера временной шкалы */
  timelineRef?: (ref: HTMLDivElement | null) => void;
}

const Timeline: React.FC<TimelineProps> = ({
  startTime,
  endTime,
  segments = [],
  handles = [],
  onHandleDrag,
  onHandleDragEnd,
  formatTime = (date: Date) => date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
  formatDuration = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (m === 0) return `${h}ч`;
    return `${h}ч ${m}м`;
  },
  minSegmentWidth = 8,
  showTimeLabels = true,
  showHandleTimeLabels,
  showDuration = true,
  showLabels = true,
  draggable = true,
  snapMinutes = 1,
  className = '',
  timelineRef
}) => {
  const [draggingHandleId, setDraggingHandleId] = useState<string | number | null>(null);
  const [tempHandlePositions, setTempHandlePositions] = useState<Map<string | number, Date>>(new Map());
  const internalTimelineRef = useRef<HTMLDivElement | null>(null);
  const [timelineElement, setTimelineElement] = useState<HTMLDivElement | null>(null);

  // Объединяем внутренний и внешний ref
  useEffect(() => {
    const element = internalTimelineRef.current;
    setTimelineElement(element);
    if (timelineRef) {
      timelineRef(element);
    }
  }, [timelineRef]);

  // Вычисляем позицию времени в процентах
  const getTimePosition = useCallback((time: Date): number => {
    const totalDuration = endTime.getTime() - startTime.getTime();
    if (totalDuration <= 0) return 0;
    const timePosition = time.getTime() - startTime.getTime();
    return (timePosition / totalDuration) * 100;
  }, [startTime, endTime]);

  // Вычисляем время из позиции в процентах
  const getTimeFromPosition = useCallback((percent: number): Date => {
    const totalDuration = endTime.getTime() - startTime.getTime();
    let timePosition = startTime.getTime() + (totalDuration * percent / 100);

    // Привязка к круглым минутам: пиксель-процентный пересчёт даёт «грязные» секунды,
    // из-за которых суммы считаются от 1ч59м28с вместо ровных 2ч
    if (snapMinutes > 0) {
      const snapMs = snapMinutes * 60 * 1000;
      timePosition = Math.round(timePosition / snapMs) * snapMs;
      // После округления не выходим за пределы смены
      timePosition = Math.max(startTime.getTime(), Math.min(endTime.getTime(), timePosition));
    }

    return new Date(timePosition);
  }, [startTime, endTime, snapMinutes]);

  // Обработка начала перетаскивания
  const handleMouseDown = useCallback((e: React.MouseEvent, handleId: string | number) => {
    if (!draggable) return;
    e.preventDefault();
    e.stopPropagation();
    setDraggingHandleId(handleId);
    const handle = handles.find(h => h.id === handleId);
    if (handle) {
      setTempHandlePositions(prev => {
        const newMap = new Map(prev);
        newMap.set(handleId, handle.time);
        return newMap;
      });
    }
  }, [handles, draggable]);

  const handleTouchStart = useCallback((e: React.TouchEvent, handleId: string | number) => {
    if (!draggable) return;
    e.preventDefault();
    e.stopPropagation();
    setDraggingHandleId(handleId);
    const handle = handles.find(h => h.id === handleId);
    if (handle) {
      setTempHandlePositions(prev => {
        const newMap = new Map(prev);
        newMap.set(handleId, handle.time);
        return newMap;
      });
    }
  }, [handles, draggable]);

  // Обработка перетаскивания
  useEffect(() => {
    if (draggingHandleId === null || !timelineElement) return;

    const rect = timelineElement.getBoundingClientRect();
    const totalDuration = endTime.getTime() - startTime.getTime();

    const updatePosition = (clientX: number) => {
      const x = clientX - rect.left;
      const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
      const newTime = getTimeFromPosition(percent);

      setTempHandlePositions(prev => {
        const newMap = new Map(prev);
        newMap.set(draggingHandleId, newTime);
        return newMap;
      });

      if (onHandleDrag) {
        onHandleDrag(draggingHandleId, newTime);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      updatePosition(e.clientX);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        updatePosition(e.touches[0].clientX);
      }
    };

    const handleMouseUp = () => {
      setTempHandlePositions(prev => {
        const finalTime = prev.get(draggingHandleId);
        if (finalTime && onHandleDragEnd) {
          onHandleDragEnd(draggingHandleId, finalTime);
        }
        const newMap = new Map(prev);
        newMap.delete(draggingHandleId);
        return newMap;
      });
      setDraggingHandleId(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [draggingHandleId, timelineElement, startTime, endTime, getTimeFromPosition, onHandleDrag, onHandleDragEnd]);

  // Вычисляем позиции точек перетаскивания с учетом временных позиций
  const handlePositions = useMemo(() => {
    return handles.map(handle => {
      const tempTime = tempHandlePositions.get(handle.id);
      const time = tempTime || handle.time;
      const position = handle.position !== undefined ? handle.position : getTimePosition(time);
      return { ...handle, time, position };
    });
  }, [handles, tempHandlePositions, getTimePosition]);

  // Вычисляем сегменты с учетом временных позиций точек перетаскивания
  const computedSegments = useMemo(() => {
    return segments.map((segment) => {
      // Используем исходные start и end из сегмента
      // Если родительский компонент обновляет сегменты при перетаскивании,
      // они будут автоматически обновлены через props
      const startPercent = getTimePosition(segment.start);
      const endPercent = getTimePosition(segment.end);
      const width = endPercent - startPercent;

      return {
        ...segment,
        startPercent,
        endPercent,
        width
      };
    });
  }, [segments, getTimePosition]);

  return (
    <div 
      className={`timeline-container ${className}`}
      ref={internalTimelineRef}
      onMouseUp={() => {
        if (draggingHandleId !== null) {
          const finalTime = tempHandlePositions.get(draggingHandleId);
          if (finalTime && onHandleDragEnd) {
            onHandleDragEnd(draggingHandleId, finalTime);
          }
          setDraggingHandleId(null);
        }
      }}
      onMouseLeave={() => {
        if (draggingHandleId !== null) {
          const finalTime = tempHandlePositions.get(draggingHandleId);
          if (finalTime && onHandleDragEnd) {
            onHandleDragEnd(draggingHandleId, finalTime);
          }
          setDraggingHandleId(null);
        }
      }}
      onTouchEnd={() => {
        if (draggingHandleId !== null) {
          const finalTime = tempHandlePositions.get(draggingHandleId);
          if (finalTime && onHandleDragEnd) {
            onHandleDragEnd(draggingHandleId, finalTime);
          }
          setDraggingHandleId(null);
        }
      }}
    >
      <div className="timeline-line">
        {showTimeLabels && (
          <>
            <div className="timeline-start-time">
              {formatTime(startTime)}
            </div>
            <div className="timeline-end-time">
              {formatTime(endTime)}
            </div>
          </>
        )}

        {/* Точки перетаскивания */}
        {handlePositions.map((handle) => (
          <div
            key={handle.id}
            className={`timeline-handle ${draggingHandleId === handle.id ? 'dragging' : ''} ${!draggable ? 'non-draggable' : ''}`}
            style={{ left: `${handle.position}%` }}
            onMouseDown={draggable ? (e) => handleMouseDown(e, handle.id) : undefined}
            onTouchStart={draggable ? (e) => handleTouchStart(e, handle.id) : undefined}
            title={(showHandleTimeLabels ?? showTimeLabels) ? formatTime(handle.time) : undefined}
          >
            <div className="timeline-handle-dot"></div>
            {(showHandleTimeLabels ?? showTimeLabels) && (
              <div className="timeline-handle-time">
                {formatTime(handle.time)}
              </div>
            )}
          </div>
        ))}

        {/* Сегменты */}
        {computedSegments.map((segment, index) => {
          const duration = segment.duration !== undefined 
            ? segment.duration 
            : (segment.end.getTime() - segment.start.getTime()) / (1000 * 60 * 60);

          // Используем минимальную ширину для маленьких сегментов, чтобы они были видны
          const displayWidth = Math.max(segment.width, minSegmentWidth);
          
          // Для очень маленьких сегментов используем минимальную ширину, но сохраняем позицию
          // Это позволяет видеть все сегменты, даже если они перекрываются визуально
          const actualLeft = segment.startPercent;
          const actualWidth = segment.width < minSegmentWidth ? minSegmentWidth : segment.width;
          
          // Для информационных окошек используем реальную ширину сегмента (без минимальной ширины)
          // чтобы они точно выравнивались по границам активностей (ползункам)
          const infoWidth = segment.width;

          return (
            <React.Fragment key={segment.id}>
              {/* Визуализация самого сегмента (цветная полоса) */}
              <div
                className="timeline-segment-bar"
                style={{
                  position: 'absolute',
                  left: `${actualLeft}%`,
                  width: `${actualWidth}%`,
                  height: '8px',
                  top: '-3px',
                  background: 'transparent',
                  borderLeft: '2px solid #3b82f6',
                  borderRight: '2px solid #3b82f6',
                  borderTop: '2px solid #3b82f6',
                  borderBottom: '2px solid #3b82f6',
                  borderRadius: '2px',
                  pointerEvents: 'none',
                  zIndex: 0
                }}
                title={segment.label}
              />
              {showLabels && segment.label && (
                <div
                  className="timeline-segment-label"
                  style={{
                    left: `${actualLeft}%`,
                    width: `${infoWidth}%`
                  }}
                  title={segment.label}
                >
                  <span className="timeline-segment-label-text">
                    {segment.label}
                  </span>
                </div>
              )}
              {showDuration && (
                <div
                  className="timeline-segment-duration"
                  style={{
                    left: `${actualLeft}%`,
                    width: `${infoWidth}%`
                  }}
                  title={`Длительность: ${formatDuration(duration)}`}
                >
                  <span className="timeline-segment-duration-text">
                    {formatDuration(duration)}
                  </span>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default Timeline;

